import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, Plus, Minus, X, Send, ArrowLeft } from 'lucide-react'
import { useEquipmentStore } from '../stores/equipmentStore'
import { useSupabaseOrderStore } from '../stores/supabaseOrderStore'
import { useAuthStore } from '../stores/authStore'
import { TeamType } from '../../types'

const teams: TeamType[] = [
  '1. Mannschaft', '2. Mannschaft', '3. Mannschaft',
  '50+', '40+', '30+',
  'BA', 'BB', 'CA', 'CB', 'FF-15', 'FF-17',
  'D9A', 'D9B', 'D9C', 'D7A', 'D7B', 'D7C',
  'EA', 'EB', 'EC', 'ED', 'FA', 'FB', 'G'
]

export default function CartPage() {
  const navigate = useNavigate()
  const { cart, removeFromCart, updateCartItem, getCartTotal, clearCart } = useEquipmentStore()
  const { createOrder } = useSupabaseOrderStore()
  const { profile, user } = useAuthStore()
  
  const [orderForm, setOrderForm] = useState({
    orderedBy: profile?.name || '',
    team: '1. Mannschaft' as TeamType,
    trainerName: '',
    deliveryMonth: 'August' as 'August' | 'Dezember',
    notes: '-'
  })
  
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const updateQuantity = (itemId: string, change: number) => {
    const item = cart.find(i => i.id === itemId)
    if (item) {
      const newQuantity = Math.max(1, item.quantity + change)
      updateCartItem(itemId, { quantity: newQuantity })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!orderForm.orderedBy.trim()) {
      newErrors.orderedBy = 'Name des Bestellers ist erforderlich'
    }
    
    if (!orderForm.trainerName.trim()) {
      newErrors.trainerName = 'Trainer/Verantwortlicher ist erforderlich'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleConfirmOrder = () => {
    if (!validateForm()) {
      return
    }
    
    if (cart.length === 0) {
      alert('Warenkorb ist leer!')
      return
    }
    
    setShowConfirmation(true)
  }

  const sendOrderEmail = async (orderData: any) => {
    // Comment out old email functionality (Outlook opening)
    /*
    const subject = `Neue Equipment-Bestellung - ${orderForm.team}`
    
    const orderItems = cart.map(item => 
      `- ${item.name}${item.size ? ` (${item.size})` : ''} x ${item.quantity}`
    ).join('\n')
    
    const emailBody = `Hallo FC Frick Team,

eine neue Equipment-Bestellung wurde über die App erstellt:

BESTELLDETAILS:
Bestellt von: ${orderForm.orderedBy}
Team: ${orderForm.team}
Trainer/Verantwortlicher: ${orderForm.trainerName}
Gewünschte Lieferung: ${orderForm.deliveryMonth}

BESTELLTE ARTIKEL:
${orderItems}

${orderForm.notes ? `ZUSÄTZLICHE INFORMATIONEN:\n${orderForm.notes}` : ''}

Bitte bearbeitet die Bestellung über das Admin-Panel.

Sportliche Grüsse
FC Frick App

---
Diese E-Mail wurde automatisch generiert.`

    // Send to material@fcfrick.ch with CC to thomas.keller@fcfrick.ch
    const primaryEmail = 'material@fcfrick.ch'
    const ccEmail = 'thomas.keller@fcfrick.ch'
    
    const mailtoLink = `mailto:${primaryEmail}?cc=${ccEmail}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`
    
    window.location.href = mailtoLink
    */

    // New Netlify email function
    try {
      const { sendEmail, createEquipmentEmail } = await import('../utils/emailService')
      const emailData = createEquipmentEmail({
        ...orderData,
        items: cart,
        orderedBy: orderForm.orderedBy,
        team: orderForm.team,
        trainerName: orderForm.trainerName,
        deliveryMonth: orderForm.deliveryMonth,
        notes: orderForm.notes
      }, user?.email || '')
      const result = await sendEmail(emailData)
      
      if (!result.success) {
        console.error('Failed to send email:', result.error)
        // Could show user notification here if needed
      } else {
        console.log('Email sent successfully via Netlify function')
      }
    } catch (error) {
      console.error('Error sending email:', error)
    }
  }

  const handleSubmitOrder = async () => {
    setIsSubmitting(true)
    
    try {
      // Erstelle die Bestellung über Supabase
      const result = await createOrder(cart, {
        orderedBy: orderForm.orderedBy.trim(),
        team: orderForm.team,
        trainerName: orderForm.trainerName.trim(),
        deliveryMonth: orderForm.deliveryMonth,
        notes: orderForm.notes.trim()
      })
      
      if (result.error) {
        throw new Error(result.error)
      }
      
      console.log('Order created successfully')
      
      // Send automated email
      await sendOrderEmail({})
      
      // Warenkorb leeren
      clearCart()
      
      // Auto-Refresh der Orders und Navigation zur Equipment-Seite
      setTimeout(() => {
        // Daten refresh für neue Bestellung
        const refreshData = async () => {
          try {
            // Import the order store to refresh data
            const { useSupabaseOrderStore } = await import('../stores/supabaseOrderStore')
            const { fetchOrders } = useSupabaseOrderStore.getState()
            await fetchOrders()
          } catch (error) {
            console.error('Error refreshing orders:', error)
          }
        }
        refreshData()
        
        // Navigate back to equipment page
        navigate('/equipment')
      }, 1500)
    } catch (error) {
      console.error('Fehler beim Erstellen der Bestellung:', error)
      alert('Fehler beim Erstellen der Bestellung. Bitte versuche es erneut.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setOrderForm(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (showConfirmation) {
    return (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <button
            onClick={() => setShowConfirmation(false)}
            style={{
              background: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              color: '#374151'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1B7E3A'
              e.currentTarget.style.backgroundColor = '#f0fdf4'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.025em'
          }}>Bestellung bestätigen</h1>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
            borderRadius: '16px 16px 0 0'
          }} />
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#111827'
          }}>Bestellbestätigung</h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div>
              <h3 style={{
                fontWeight: '700',
                marginBottom: '12px',
                color: '#111827'
              }}>Bestelldetails</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                fontSize: '14px'
              }}>
                <div><strong>Besteller:</strong> {orderForm.orderedBy}</div>
                <div><strong>Team:</strong> {orderForm.team}</div>
                <div><strong>Trainer:</strong> {orderForm.trainerName}</div>
                <div><strong>Lieferung:</strong> {orderForm.deliveryMonth}</div>
                {orderForm.notes && <div><strong>Bemerkungen:</strong> {orderForm.notes}</div>}
              </div>
            </div>
            
            <div>
              <h3 style={{
                fontWeight: '700',
                marginBottom: '12px',
                color: '#111827'
              }}>Bestellsummary</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {cart.map(item => (
                  <div key={item.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '14px'
                  }}>
                    <span>{item.name} {item.size ? `(${item.size})` : ''}</span>
                    <span>{item.quantity}x</span>
                  </div>
                ))}
                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '8px',
                  fontWeight: '700'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>Gesamt:</span>
                    <span>CHF {getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            border: '1px solid #93c5fd',
            color: '#1e40af',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '16px'
          }}>
            <p><strong>Hinweis:</strong> Diese Bestellung wird zur Genehmigung an die Vereinsleitung weitergeleitet. Du erhältst eine Bestätigung, sobald die Bestellung bearbeitet wurde.</p>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            <button
              onClick={() => setShowConfirmation(false)}
              style={{
                background: 'transparent',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1',
                transition: 'all 0.2s ease',
                color: '#374151'
              }}
              disabled={isSubmitting}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.borderColor = '#1B7E3A'
                  e.currentTarget.style.backgroundColor = '#f0fdf4'
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }
              }}
            >
              Zurück bearbeiten
            </button>
            <button
              onClick={handleSubmitOrder}
              style={{
                background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: isSubmitting ? 'none' : '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
              }}
              disabled={isSubmitting}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                }
              }}
            >
              {isSubmitting ? (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <Send size={16} />
              )}
              {isSubmitting ? 'Wird gesendet...' : 'Bestellung definitiv senden'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <button
          onClick={() => navigate('/equipment')}
          style={{
            background: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            color: '#374151'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#1B7E3A'
            e.currentTarget.style.backgroundColor = '#f0fdf4'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db'
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <ArrowLeft size={16} />
          Zurück zum Equipment
        </button>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.025em'
          }}>Warenkorb</h1>
          <p style={{
            color: '#6b7280',
            fontSize: '18px',
            fontWeight: '500'
          }}>Deine ausgewählten Artikel</p>
        </div>
      </div>

      {cart.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '64px 32px',
          background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
          borderRadius: '16px',
          border: '1px solid #e5e7eb'
        }}>
          <ShoppingCart size={64} style={{ margin: '0 auto 1rem', color: '#d1d5db' }} />
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '8px',
            color: '#111827'
          }}>Warenkorb ist leer</h3>
          <p style={{
            color: '#6b7280',
            marginBottom: '24px'
          }}>Füge Artikel aus dem Equipment-Bereich hinzu.</p>
          <button
            onClick={() => navigate('/equipment')}
            style={{
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
          >
            Zum Equipment
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px'
        }}>
          {/* Warenkorb Items */}
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              padding: '24px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative gradient overlay */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
                borderRadius: '16px 16px 0 0'
              }} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#111827'
              }}>Artikel im Warenkorb ({cart.length})</h2>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {cart.map(item => (
                  <div key={item.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    background: '#ffffff',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}>
                    <div style={{ flex: '1' }}>
                      <h4 style={{
                        fontWeight: '500',
                        marginBottom: '4px',
                        color: '#111827'
                      }}>{item.name}</h4>
                      {item.size && <p style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginBottom: '4px'
                      }}>Größe: {item.size}</p>}
                      <p style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#111827'
                      }}>
                        {item.price ? `CHF ${item.price.toFixed(2)}` : 'Preis auf Anfrage'}
                      </p>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          color: '#374151'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#1B7E3A'
                          e.currentTarget.style.backgroundColor = '#f0fdf4'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#d1d5db'
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        <Minus size={16} />
                      </button>
                      <span style={{
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        textAlign: 'center',
                        minWidth: '48px',
                        fontWeight: '600'
                      }}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          color: '#374151'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#1B7E3A'
                          e.currentTarget.style.backgroundColor = '#f0fdf4'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#d1d5db'
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontWeight: '700',
                        marginBottom: '8px',
                        color: '#111827'
                      }}>
                        {item.price ? `CHF ${(item.price * item.quantity).toFixed(2)}` : 'Preis auf Anfrage'}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #dc2626',
                          borderRadius: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          color: '#dc2626'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{
                borderTop: '1px solid #e5e7eb',
                paddingTop: '16px',
                marginTop: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '700',
                  fontSize: '18px',
                  marginBottom: '12px',
                  color: '#111827'
                }}>
                  <span>Gesamtsumme:</span>
                  <span>CHF {getCartTotal().toFixed(2)}</span>
                </div>
                
                <button
                  onClick={clearCart}
                  style={{
                    background: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'all 0.2s ease',
                    color: '#374151'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#dc2626'
                    e.currentTarget.style.backgroundColor = '#fef2f2'
                    e.currentTarget.style.color = '#dc2626'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = '#374151'
                  }}
                >
                  Warenkorb leeren
                </button>
              </div>
            </div>
          </div>

          {/* Bestellformular */}
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              padding: '24px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative gradient overlay */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
                borderRadius: '16px 16px 0 0'
              }} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#111827'
              }}>Bestellinformationen</h2>
              
              <form style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    marginBottom: '6px',
                    color: '#374151'
                  }}>Besteller *</label>
                  <input
                    type="text"
                    value={orderForm.orderedBy}
                    onChange={(e) => handleInputChange('orderedBy', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '12px',
                      border: errors.orderedBy ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Dein Name"
                    onFocus={(e) => {
                      if (!errors.orderedBy) {
                        e.currentTarget.style.borderColor = '#1B7E3A'
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                      }
                    }}
                    onBlur={(e) => {
                      if (!errors.orderedBy) {
                        e.currentTarget.style.borderColor = '#d1d5db'
                        e.currentTarget.style.boxShadow = 'none'
                      }
                    }}
                  />
                  {errors.orderedBy && <span style={{
                    color: '#ef4444',
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'block'
                  }}>{errors.orderedBy}</span>}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    marginBottom: '6px',
                    color: '#374151'
                  }}>Team *</label>
                  <select
                    value={orderForm.team}
                    onChange={(e) => handleInputChange('team', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    {teams.map(team => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    marginBottom: '6px',
                    color: '#374151'
                  }}>Trainer/Verantwortlicher *</label>
                  <input
                    type="text"
                    value={orderForm.trainerName}
                    onChange={(e) => handleInputChange('trainerName', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '12px',
                      border: errors.trainerName ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Name des Trainers"
                    onFocus={(e) => {
                      if (!errors.trainerName) {
                        e.currentTarget.style.borderColor = '#1B7E3A'
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                      }
                    }}
                    onBlur={(e) => {
                      if (!errors.trainerName) {
                        e.currentTarget.style.borderColor = '#d1d5db'
                        e.currentTarget.style.boxShadow = 'none'
                      }
                    }}
                  />
                  {errors.trainerName && <span style={{
                    color: '#ef4444',
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'block'
                  }}>{errors.trainerName}</span>}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    marginBottom: '6px',
                    color: '#374151'
                  }}>Gewünschte Lieferung</label>
                  <select
                    value={orderForm.deliveryMonth}
                    onChange={(e) => handleInputChange('deliveryMonth', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <option value="August">August</option>
                    <option value="Dezember">Dezember</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    marginBottom: '6px',
                    color: '#374151'
                  }}>Bemerkungen</label>
                  <textarea
                    value={orderForm.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                    rows={3}
                    placeholder="Zusätzliche Informationen zur Bestellung..."
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleConfirmOrder}
                  style={{
                    background: cart.length === 0 ? '#9ca3af' : 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: cart.length === 0 ? 'none' : '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                  }}
                  disabled={cart.length === 0}
                  onMouseEnter={(e) => {
                    if (cart.length > 0) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (cart.length > 0) {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                    }
                  }}
                >
                  <Send size={16} />
                  Bestellung aufgeben
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      <style>
        {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
      </style>
    </div>
  )
}
